<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import {
    IMenuChoice,
    menuChoiceName,
    menuChoices,
    TMenuOrderType,
  } from '@/domain/catalog/menu-choices';

  /**
   * «Menú: {nombre} ▾» (plan §5.16, multi-menu stores at any tier): the base
   * menu and the store's live menus, each with the order types that use it.
   */
  const { state, data, url } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const choices = computed(() =>
    menuChoices(data.menus.value, data.restaurant.value?.settings ?? null)
  );

  const current = computed(
    () => choices.value.find((choice) => choice.id === state.menuId) ?? null
  );

  function nameOf(choice: IMenuChoice | null): string {
    const name = menuChoiceName(choices.value, choice?.id ?? null);
    if (name.kind === 'named') return name.name;
    if (name.kind === 'unnamed')
      return t('menuPage.menus.unnamed', { n: name.position });
    return t('menuPage.menus.main');
  }

  /** «Menú principal», or «Menú: Almuerzo». */
  const triggerText = computed(() =>
    current.value && current.value.id !== null
      ? t('menuPage.menus.current', { name: nameOf(current.value) })
      : t('menuPage.menus.main')
  );

  const ORDER_TYPE_KEYS: Record<TMenuOrderType, string> = {
    seat: 'order.type.seat',
    away: 'order.type.away',
    delivery: 'order.type.delivery',
  };

  function usageOf(choice: IMenuChoice): string {
    if (!choice.orderTypes.length) return t('menuPage.menus.unused');
    return choice.orderTypes
      .map((type) => t(ORDER_TYPE_KEYS[type]))
      .join(' · ');
  }

  function choose(choice: IMenuChoice, close: () => void) {
    close();
    if (choice.id !== state.menuId) url.setMenu(choice.id);
  }
</script>

<template>
  <PosUiPopover
    class="v2-catalog-menu-switcher"
    placement="bottom"
    align="start"
  >
    <template #trigger="{ open, toggle }">
      <button
        type="button"
        class="switcher-trigger"
        aria-haspopup="true"
        :aria-expanded="open ? 'true' : 'false'"
        @click="toggle"
      >
        <span class="trigger-label">{{ triggerText }}</span>
        <i class="mdi mdi-chevron-down trigger-icon" />
      </button>
    </template>
    <template #default="{ close }">
      <div class="switcher-list" role="menu">
        <button
          v-for="choice in choices"
          :key="choice.id === null ? 'main' : choice.id"
          type="button"
          role="menuitemradio"
          class="switcher-option"
          :class="{ 'is-current': choice.id === state.menuId }"
          :aria-checked="choice.id === state.menuId ? 'true' : 'false'"
          @click="choose(choice, close)"
        >
          <span class="option-text">
            <span class="option-name">{{ nameOf(choice) }}</span>
            <span class="option-usage">{{ usageOf(choice) }}</span>
          </span>
          <i
            v-if="choice.id === state.menuId"
            class="mdi mdi-check option-check"
          />
        </button>
      </div>
    </template>
  </PosUiPopover>
</template>

<style scoped lang="scss">
  // The label lines up with the title; the hover pill bleeds left. On the
  // root: a negative margin on a shrink-to-fit child would truncate it.
  .v2-catalog-menu-switcher {
    @apply tw-max-w-full;
    margin-left: -8px;

    .switcher-trigger {
      @apply tw-relative tw-mt-1 tw-inline-flex tw-h-8 tw-max-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-2 tw-text-sm tw-font-semibold tw-text-neutral-800;

      &:hover {
        @apply tw-bg-neutral-900/5;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      // 32 px visual, 44 px to the touch.
      &::after {
        @apply tw-absolute tw-left-0 tw-right-0;
        content: '';
        top: -6px;
        bottom: -6px;
      }

      .trigger-label {
        @apply tw-truncate;
      }

      .trigger-icon {
        @apply tw-ml-1 tw-text-lg tw-leading-none tw-text-neutral-500;
      }
    }

    .switcher-list {
      @apply -tw-mx-1.5 -tw-my-1.5 tw-flex tw-flex-col;
      width: 260px;
      max-width: calc(100vw - 48px);
    }

    .switcher-option {
      @apply tw-flex tw-min-h-[52px] tw-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left;

      &:hover {
        @apply tw-bg-neutral-100;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      &.is-current {
        @apply tw-bg-orange-50;
      }

      .option-text {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;
      }

      .option-name {
        @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
      }

      .option-usage {
        @apply tw-truncate tw-text-xs tw-text-neutral-500;
      }

      .option-check {
        @apply tw-ml-2 tw-text-lg tw-leading-none tw-text-orange-600;
      }
    }
  }
</style>
